import React, { useContext, useEffect, useState } from "react";
import { ActivityIndicator } from "react-native";
import { keyBy, find } from "lodash";

import { useExternalState } from "../../utils/me";
import { FurnitureContext } from "../../utils/furniture";
import { useNearbyResources } from "../../utils/resources";
import { useResourceCategories } from "../../utils/contentful";
import { template } from "../../utils/locale";
import Button from "../../components/Button";
import CategoryListItem from "../../components/CategoryListItem";
import { NavContext } from "../../utils/nav";
import Redo from "../../components/Redo";
import {
    Container,
    Body,
    Title,
    ResourceCategoryList,
    NoResultsContainer,
    NoResultsMessage,
    RedoContainer,
    SearchFormContainer,
    TextInput,
    KeyboardAvoidingView,
    EnterZipCodeLabel,
    ZipCodeUsage,
} from "./styles";

const ResourcesNearMe = ({ navigation }) => {
    const [title, zip, resourceCategoriesNDVH, setZip, clearZip] =
        useNearbyResources();
    const [{ data: referenceResourceCategories }] = useResourceCategories();
    const { setLeafNav } = useContext(NavContext);
    const {
        labelChange,
        strings: { resourceLabel, resourcesLabel },
    } = useContext(FurnitureContext);
    const [contentStatesMap, setContentsStateMap] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const {
        contentStates,
        api: { markContentAsRead },
    } = useExternalState();

    useEffect(() => {
        setLeafNav(navigation);
    }, [navigation]);

    useEffect(() => {
        // if a content id appears in the content state map, it has been accessed or is Not New
        const _contentStatesMap = keyBy(contentStates, "contentId");
        setContentsStateMap(_contentStatesMap);
    }, [contentStates]);

    useEffect(() => {
        setIsLoading(!resourceCategoriesNDVH);
        setShowForm(!zip);
    });

    const hideForm = !showForm;

    const onSaveZip = zip => {
        setZip(zip);
        setIsLoading(true);
    };

    let _mappedResourceCategories = [];

    if (resourceCategoriesNDVH?.length) {
        _mappedResourceCategories = resourceCategoriesNDVH.map(cat => {
            const catSulg = cat.slug;
            let newCategory;

            const matchingCategory = find(referenceResourceCategories, [
                "slug",
                catSulg,
            ]);

            if (matchingCategory) {
                newCategory = {
                    ...cat,
                    category: matchingCategory.category,
                    icon: matchingCategory.icon,
                };
            } else {
                newCategory = cat;
            }
            return newCategory;
        });
    }

    return (
        <Container>
            <RedoContainer hidden={showForm}>
                <Redo label={labelChange} onPress={() => clearZip()} />
            </RedoContainer>
            <Body>
                <Title type="h3">{title}</Title>
                <SearchForm hidden={hideForm} value={zip} onSave={onSaveZip} />
                <ResourceCategoriesContainer
                    hidden={showForm}
                    isLoading={isLoading}>
                    <ResourceCategoryList>
                        {!_mappedResourceCategories?.length && (
                            <NoResults zip={zip} />
                        )}
                        {_mappedResourceCategories &&
                            _mappedResourceCategories?.map(resourceCategory => {
                                console.log(resourceCategory.icon);
                                return (
                                    <CategoryListItem
                                        key={resourceCategory._id}
                                        item={resourceCategory}
                                        onPress={() => {
                                            markContentAsRead(
                                                resourceCategory._id,
                                            );
                                            navigation.push(
                                                "ResourceCategoryNearMe",
                                                {
                                                    resourceCategoryId:
                                                        resourceCategory._id,
                                                    data: resourceCategory,
                                                },
                                            );
                                        }}
                                        count={
                                            resourceCategory.resources.length
                                        }
                                        singular={resourceLabel}
                                        plural={resourcesLabel}
                                        icon={"ICON_SELF_CARE"}
                                        payload={{ resourceCategory }}
                                        hasNew={
                                            !contentStatesMap?.[
                                                resourceCategory._id
                                            ]
                                        }
                                    />
                                );
                            })}
                    </ResourceCategoryList>
                </ResourceCategoriesContainer>
            </Body>
        </Container>
    );
};

export default ResourcesNearMe;

const ResourceCategoriesContainer = ({
    hidden = false,
    isLoading,
    children,
}) => {
    if (hidden) return null;
    if (isLoading) return <ActivityIndicator size="large" />;
    return <>{children}</>;
};

const NoResults = ({ zip }) => {
    const {
        strings: { noResultsFoundInZip },
    } = useContext(FurnitureContext);

    const getMessageWithZip: (values: { zip: string }) => string =
        template(noResultsFoundInZip);

    return (
        <NoResultsContainer>
            <NoResultsMessage>{getMessageWithZip({ zip })}</NoResultsMessage>
        </NoResultsContainer>
    );
};

const SearchForm = ({ hidden = false, value, onSave }) => {
    const [localValue, onChangeText] = useState(null);
    const {
        labelSave,
        strings: { pleaseEnterZip, zipCodeUsage },
    } = useContext(FurnitureContext);
    const onPress = () => onSave(localValue);

    if (hidden) return null;

    return (
        <SearchFormContainer>
            <KeyboardAvoidingView>
                <EnterZipCodeLabel type="body2">
                    {pleaseEnterZip}
                </EnterZipCodeLabel>
                <TextInput
                    autoCompleteType="postal-code"
                    value={value}
                    onChangeText={onChangeText}
                    maxLength={5}
                    keyboardType="number-pad"
                />
                <Button title={labelSave} onPress={onPress}></Button>
            </KeyboardAvoidingView>
            <ZipCodeUsage>{zipCodeUsage}</ZipCodeUsage>
        </SearchFormContainer>
    );
};
